import type { DependencyList } from 'react';
import styled from 'styled-components';

import { LabelS } from '#src/atoms/typography/index.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { useShortcut } from './useShortcut.ts';

const Key = styled(LabelS).attrs({
  as: 'small',
  bold: true,
})`
  display: none;
  @media ${laptopAndAbove} {
    display: inline-flex;
    padding: 0 6px; /* custom value because it's an icon */
    margin-left: var(--unit);
    border: solid currentColor;
    border-radius: var(--half-unit);
    border-width: 1px 2px 4px;
    color: var(--c-nd-200);
  }
`;

// lib.dom has no types for userAgentData yet
const { userAgentData } = navigator as Navigator & {
  userAgentData?: { platform?: string };
};

const isMac = /mac/i.test(
  userAgentData?.platform ||
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    navigator.platform
);

const unmodifiedKey = (key: string) => (event: KeyboardEvent) =>
  event.key === key && !event.metaKey && !event.ctrlKey && !event.altKey;

const SHORTCUTS = {
  cmdk: {
    predicate: (event: KeyboardEvent) => {
      return event.key === 'k' && (event.metaKey || event.ctrlKey);
    },
    label: isMac ? '⌘+k' : 'ctrl+k',
  },
  '1': {
    predicate: (event: KeyboardEvent) => event.code === 'Digit1',
    label: '1',
  },
  '2': {
    predicate: (event: KeyboardEvent) => event.code === 'Digit2',
    label: '2',
  },
  '3': {
    predicate: (event: KeyboardEvent) => event.code === 'Digit3',
    label: '3',
  },
  '4': {
    predicate: (event: KeyboardEvent) => event.code === 'Digit4',
    label: '4',
  },
  '5': {
    predicate: (event: KeyboardEvent) => event.code === 'Digit5',
    label: '5',
  },
  down: {
    predicate: (event: KeyboardEvent) => event.code === 'ArrowDown',
    label: '↓',
  },
  up: {
    predicate: (event: KeyboardEvent) => event.code === 'ArrowUp',
    label: '↑',
  },
  f: { predicate: unmodifiedKey('f'), label: 'f' },
  s: { predicate: unmodifiedKey('s'), label: 's' },
  u: { predicate: unmodifiedKey('u'), label: 'u' },
  n: { predicate: unmodifiedKey('n'), label: 'n' },
  a: { predicate: unmodifiedKey('a'), label: 'a' },
  i: { predicate: unmodifiedKey('i'), label: 'i' },
  c: { predicate: unmodifiedKey('c'), label: 'c' },
  g: { predicate: unmodifiedKey('g'), label: 'g' },
  l: { predicate: unmodifiedKey('l'), label: 'l' },
  h: { predicate: unmodifiedKey('h'), label: 'h' },
  x: { predicate: unmodifiedKey('x'), label: 'x' },
};

export const useShortcutWithKeys = (
  shortcut: keyof typeof SHORTCUTS,
  callback: () => void,
  deps?: DependencyList,
  skip?: boolean
) => {
  useShortcut(skip ? undefined : SHORTCUTS[shortcut].predicate, callback, deps);

  return {
    Icon: <Key>{SHORTCUTS[shortcut].label}</Key>,
  };
};
